import { createElement, useEffect, useRef, useCallback } from 'react';
import { flushSync } from 'react-dom';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '@store/index';
import { selectIsAuthenticated, logout, sessionExpired, validateSession } from '@features/auth/authSlice';
import { showNotification } from '@features/ui/uiSlice';
import { SESSION_EXPIRED_EVENT } from '@services/api';
import { clearGlbCache } from '@services/cadGlbCache';
import { LocalizedToastText } from '@components/common/LocalizedToastText';

const SESSION_CHECK_INTERVAL = 5 * 60 * 1000; // Check every 5 minutes
const INACTIVITY_TIMEOUT = 30 * 60 * 1000; // 30 minutes of inactivity
const WARNING_BEFORE_LOGOUT = 5 * 60 * 1000; // Warn 5 minutes before logout
// Stable id so the warning can be dismissed the moment the session ends — sonner
// pauses a toast's own duration timer while the tab is blurred (exactly the
// inactivity case), so it must be dismissed explicitly, not left to expire.
const SESSION_WARNING_TOAST_ID = 'session-inactivity-warning';

export const useSessionManager = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const isAuthenticated = useAppSelector(selectIsAuthenticated);

  const lastActivityRef = useRef(Date.now());
  const warningShownRef = useRef(false);
  // Read at click time by the warning's action, which captured a stale closure
  // when the toast was raised — a dep on the callback wouldn't update it.
  const isAuthenticatedRef = useRef(isAuthenticated);
  isAuthenticatedRef.current = isAuthenticated;
  // Same reason, other direction: under BrowserRouter `navigate` is a NEW function
  // on every location change, and as a dependency it re-ran the activity effect —
  // whose first statement resets the idle clock — on every navigation. Read
  // through a ref so the effect only ever re-arms on an auth transition.
  const navigateRef = useRef(navigate);
  navigateRef.current = navigate;
  const sessionCheckIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const inactivityTimerRef = useRef<NodeJS.Timeout | null>(null);

  // Update last activity time
  const updateActivity = useCallback(() => {
    lastActivityRef.current = Date.now();
    warningShownRef.current = false;

    // Activity has to disarm the logout the warning already scheduled. Resetting
    // the clock alone leaves that timer running, and it fires on the original
    // deadline regardless of anything done since.
    if (inactivityTimerRef.current) {
      clearTimeout(inactivityTimerRef.current);
      inactivityTimerRef.current = null;
    }
  }, []);

  // Handle logout due to inactivity
  const handleInactiveLogout = useCallback(async () => {
    // Kill the warning first: its action can still "extend" a session that is
    // about to be gone, and it must not linger past the logout it predicted.
    toast.dismiss(SESSION_WARNING_TOAST_ID);
    warningShownRef.current = false;
    dispatch(showNotification({
      messageKey: 'session.expiredDueToInactivity',
      type: 'warning',
      category: 'session',
    }));
    await dispatch(logout());
    navigateRef.current('/login');
  }, [dispatch]);

  // Extend session (for manual action, and offered by the warning below)
  const extendSession = useCallback(() => {
    // The warning is being acted on — retire it either way.
    toast.dismiss(SESSION_WARNING_TOAST_ID);
    // If the session already ended (the toast outlived the logout, or auth
    // dropped via another path), do not claim it was extended.
    if (!isAuthenticatedRef.current) return;
    updateActivity();
    dispatch(showNotification({
      messageKey: 'session.extended',
      type: 'success',
      category: 'session',
    }));
  }, [updateActivity, dispatch]);

  // Show warning before logout
  const showInactivityWarning = useCallback(() => {
    if (warningShownRef.current) return;
    warningShownRef.current = true;

    // Raised through sonner rather than the Redux queue because it carries a
    // callback, and a callback has no business in serializable state. It also
    // has to outlive the default dismiss: a warning that disappears before the
    // logout it predicts leaves the user no way to act on it.
    // Nodes, not strings — see LocalizedToastText. This toast lives for
    // WARNING_BEFORE_LOGOUT and activity does not dismiss it.
    toast.warning(createElement(LocalizedToastText, { messageKey: 'session.expiresSoon' }), {
      id: SESSION_WARNING_TOAST_ID,
      duration: WARNING_BEFORE_LOGOUT,
      action: {
        label: createElement(LocalizedToastText, { messageKey: 'session.staySignedIn' }),
        onClick: extendSession,
      },
    });
  }, [extendSession]);

  // Check for inactivity
  const checkInactivity = useCallback(() => {
    if (!isAuthenticated) return;

    const now = Date.now();
    const timeSinceLastActivity = now - lastActivityRef.current;

    if (timeSinceLastActivity >= INACTIVITY_TIMEOUT) {
      // The armed timer is due at this same moment; without clearing it the
      // logout runs twice.
      if (inactivityTimerRef.current) {
        clearTimeout(inactivityTimerRef.current);
        inactivityTimerRef.current = null;
      }
      handleInactiveLogout();
    } else if (timeSinceLastActivity >= INACTIVITY_TIMEOUT - WARNING_BEFORE_LOGOUT) {
      showInactivityWarning();

      // Set timer for final logout
      if (inactivityTimerRef.current) {
        clearTimeout(inactivityTimerRef.current);
      }

      const timeUntilLogout = INACTIVITY_TIMEOUT - timeSinceLastActivity;
      inactivityTimerRef.current = setTimeout(handleInactiveLogout, timeUntilLogout);
    }
  }, [isAuthenticated, handleInactiveLogout, showInactivityWarning]);

  // Validate session periodically
  const checkSession = useCallback(async () => {
    if (!isAuthenticated) return;

    try {
      const isValid = await dispatch(validateSession()).unwrap();
      if (!isValid) {
        const publicPaths = ['/login', '/forgot-password', '/reset-password'];
        const isPublicRoute = publicPaths.some(path => window.location.pathname.startsWith(path));
        if (!isPublicRoute) {
          dispatch(showNotification({
            messageKey: 'session.expired',
            type: 'error',
            category: 'session',
          }));
          navigateRef.current('/login');
        }
      }
    } catch (error) {
      // Session validation failed, likely due to network error
      console.error('Session validation failed:', error);
    }
  }, [dispatch, isAuthenticated]);

  // Set up activity listeners
  useEffect(() => {
    if (!isAuthenticated) return;

    // Nothing tracks activity while signed out, so lastActivityRef still holds
    // the timestamp that caused the previous logout. Signing back in has to
    // start the clock fresh, or the initial check below expires the new session
    // before the user can touch anything.
    updateActivity();

    // Activity events to track
    const activityEvents = [
      'mousedown',
      'mousemove',
      'keydown',
      'scroll',
      'touchstart',
      'click',
    ];

    // Add activity listeners
    activityEvents.forEach(event => {
      window.addEventListener(event, updateActivity);
    });

    // Set up periodic checks
    sessionCheckIntervalRef.current = setInterval(() => {
      checkSession();
      checkInactivity();
    }, SESSION_CHECK_INTERVAL);

    // Initial checks
    checkSession();
    checkInactivity();

    // Cleanup
    return () => {
      activityEvents.forEach(event => {
        window.removeEventListener(event, updateActivity);
      });

      if (sessionCheckIntervalRef.current) {
        clearInterval(sessionCheckIntervalRef.current);
      }

      if (inactivityTimerRef.current) {
        clearTimeout(inactivityTimerRef.current);
      }
    };
  }, [isAuthenticated, updateActivity, checkSession, checkInactivity]);

  // Whenever the session ends by any route (inactivity, API 401, manual logout),
  // the warning toast must not survive it — a stale warning is how the user ends
  // up "extending" an already-dead session.
  useEffect(() => {
    if (!isAuthenticated) {
      toast.dismiss(SESSION_WARNING_TOAST_ID);
      warningShownRef.current = false;
    }
  }, [isAuthenticated]);

  // Handle page visibility changes. Returning to the tab is NOT user input: it
  // re-validates the session and re-evaluates the idle clock (an overdue session
  // logs out at once, one inside the warning window is warned) but never resets
  // it — this client timer is the only idle control there is.
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden && isAuthenticated) {
        checkSession();
        checkInactivity();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [checkSession, checkInactivity, isAuthenticated]);

  // Handle API-level refresh failures inside the SPA so login can recover without a page reload.
  useEffect(() => {
    const handleSessionExpired = (event: Event) => {
      event.preventDefault();
      clearGlbCache();
      flushSync(() => {
        dispatch(sessionExpired());
      });
      navigate('/login', { replace: true });
    };

    window.addEventListener(SESSION_EXPIRED_EVENT, handleSessionExpired);

    return () => {
      window.removeEventListener(SESSION_EXPIRED_EVENT, handleSessionExpired);
    };
  }, [dispatch, navigate]);

  return {
    extendSession,
  };
};
